import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailLoyaltyTransactions,
  type LoyaltyTransaction,
  type LoyaltyTransactionType,
} from '@/api/retailLoyaltyTransactions';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.customer_card.manage';

const TYPE_LABEL: Record<string, string> = {
  accrual: '+ Нарахування',
  writeoff: '− Списання',
};

const fmt = (v: string | null) =>
  v
    ? Number(v).toLocaleString('uk-UA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '';

export default function LoyaltyTransactionsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-loyalty-transactions', orgId, typeFilter],
    queryFn: () =>
      retailLoyaltyTransactions.list({
        organization_id: orgId || undefined,
        transaction_type: typeFilter ? (typeFilter as LoyaltyTransactionType) : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<LoyaltyTransaction>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'transaction_type',
        header: 'Тип',
        width: 150,
        sortValue: (r) => r.transaction_type ?? '',
        cell: (r) => TYPE_LABEL[r.transaction_type] ?? r.transaction_type,
      },
      {
        key: 'customer_card_id',
        header: 'Картка',
        sortValue: (r) => r.customer_card_id ?? '',
        cell: (r) => r.customer_card_id,
      },
      {
        key: 'amount',
        header: 'Сума',
        width: 130,
        align: 'right',
        sortValue: (r) => Number(r.amount),
        cell: (r) => fmt(r.amount),
      },
      {
        key: 'reason',
        header: 'Причина',
        width: 180,
        sortValue: (r) => r.reason ?? '',
        cell: (r) => r.reason,
      },
      {
        key: 'balance_after',
        header: 'Баланс після',
        width: 150,
        align: 'right',
        sortValue: (r) => Number(r.balance_after),
        cell: (r) => fmt(r.balance_after),
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати бонусні транзакції (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Бонусні транзакції
          <HintBadge
            title="Що це?"
            text={
              'Журнал РУХУ бонусів по картках клієнтів. Кожна транзакція ' +
              'або нараховує (+), або списує (−) бонуси.\n\n' +
              'Два типи:\n' +
              '• Нарахування — додає бонуси (день народження, промо-акція, ручне нарахування).\n' +
              '• Списання — забирає бонуси (закінчився термін, корекція, блокування підозри).\n\n' +
              'Автоматичні транзакції (НЕ створюються тут):\n' +
              '• При продажу → автоматичне нарахування за % з «Налаштувань лояльності».\n' +
              '• При списанні бонусів на касі → автоматичне списання.\n' +
              '• При поверненні товару → реверс нарахування.\n\n' +
              'Тут ствюрюються тільки РУЧНI транзакції (адміном/менеджером).'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі типи</option>
          <option value="accrual">Нарахування</option>
          <option value="writeoff">Списання</option>
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/loyalty-transactions/new')}
        >
          + Нова транзакція
        </button>
      </div>
      <DataTable<LoyaltyTransaction>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає транзакцій."
        onRowDoubleClick={(r) => navigate(`/retail/loyalty-transactions/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
